import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { supabase } from './supabase'; // Assuming you created this file

import './App.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import HomePage from './HomePage';
import DashboardPage from './DashboardPage';
import PrivacyPage from './PrivacyPage';
import TermsPage from './TermsPage';
import AuthCallback from './AuthCallback'; // Add this new component
import AddProductPage from './onboarding/AddProductPage';
import { ProductProvider } from './product/ProductContext';
import { BillingProvider } from './billing/BillingContext';

// Dev-only, opt-in: skip the auth flow and act as a signed-in user by setting
// REACT_APP_DEV_AUTO_SIGN_IN=true. Off by default so the landing page and real
// Google / email sign in can be tested locally. The fake user has no Supabase
// token, so backend features that need an account only work with a backend
// running without Supabase (local JSON storage).
const AUTO_SIGN_IN = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_AUTO_SIGN_IN === 'true';
const DEV_USER = { id: 'dev-user', email: 'dev@local.test' };

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Token refreshes hand back a new object for the same user; keeping the old
    // one stops the whole app from reloading its data each time
    const applyUser = (sessionUser) => {
      const next = sessionUser ?? (AUTO_SIGN_IN ? DEV_USER : null);
      setUser(prev => (prev?.id === next?.id ? prev : next));
    };

    // Get initial session
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      applyUser(session?.user);
      setLoading(false);
    };
    getSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        applyUser(session?.user);
        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => subscription.unsubscribe();
  }, []);

  // Pages render while the session is checked: the app pages show their own
  // loading state in place, so nothing is swapped in once it resolves
  return (
    <Router>
      <ProductProvider user={user}>
        <BillingProvider user={user}>
        <Routes>
          <Route
            path="/"
            element={<HomePage user={user} />}
          />
          <Route
            path="/dashboard/*"
            element={<DashboardPage user={user} authLoading={loading} />}
          />
          <Route
            path="/products/new"
            element={<AddProductPage user={user} authLoading={loading} />}
          />
          <Route
            path="/products/:productId/setup"
            element={<AddProductPage user={user} authLoading={loading} />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPage />}
          />
          <Route
            path="/terms"
            element={<TermsPage />}
          />
          <Route
            path="/auth/callback"
            element={<AuthCallback />}
          />
        </Routes>
        </BillingProvider>
      </ProductProvider>
    </Router>
  );
}

export default App;